import {
  IUserData,
  IRequisitionData,
  RequisitionStatusEnum,
  IRequisitionCommentData,
  IAttachment,
  IRequisitionLineData
} from '@/models';
import {
  IRequisitionService,
  IRequisitionPaginatedListData,
  REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS,
  IRequisitionExportFilterParams,
  IRequisitionFilterParam
} from './requisition.types';
import { AxiosInstance } from 'axios';
import { objectToFormData } from '@/utils/objectToFormData';
import { Transition } from '@/models/transition';

export class RequisitionService implements IRequisitionService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/requisitions/';
  }
  // TODO: LH - 2022-12-22 - let's encapsulate these paramaters
  fetchRequisitions(
    filter?: IRequisitionFilterParam
  ): Promise<IRequisitionPaginatedListData> {
    return this.axios
      .get<IRequisitionPaginatedListData>(`${this.endpoint}`, {
        timeout: 1 * (60 * 1000), //1 minute
        params: filter
      })
      .then(({ data }) => data);
  }

  fetchRequisition(id: number): Promise<IRequisitionData> {
    return this.axios
      .get<IRequisitionData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createRequisition(data: IRequisitionData): Promise<IRequisitionData> {
    const formData = objectToFormData(data);

    return this.axios
      .post<IRequisitionData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateRequisition(data: IRequisitionData): Promise<IRequisitionData> {
    const formData = objectToFormData(data);

    return this.axios
      .patch<IRequisitionData>(`${this.endpoint}${data.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  deleteRequisition(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  updateRequisitionLine(
    requisitionId: number,
    data: IRequisitionLineData
  ): Promise<IRequisitionLineData> {
    return this.axios
      .patch<IRequisitionLineData>(
        `${this.endpoint}${requisitionId}/lines/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteRequisitionAttachment(
    requisitionId: number,
    attachmentId: number
  ): Promise<any> {
    return this.axios
      .delete(
        `${this.endpoint}${requisitionId}/remove-attachment/${attachmentId}/`
      )
      .then(({ data }) => data);
  }

  updateRequisitionState(
    id: number,
    state: RequisitionStatusEnum,
    assignee?: number,
    comment?: string
  ): Promise<IRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/set_state/${state}/`, {
        assignee,
        comment
      })
      .then(({ data }) => data);
  }

  fetchRequisitionApprovers(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/approvers/`)
      .then(({ data }) => data);
  }

  fetchRequisitionBuyers(id: number): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(`${this.endpoint}${id}/buyers/`)
      .then(({ data }) => data);
  }

  assignRequisitionBuyer(id: number, buyer: number): Promise<IRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_buyer/`, {
        buyer
      })
      .then(({ data }) => data);
  }

  updateRequisitionOwner(id: number, owner: number): Promise<IRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/set-owner/`, {
        owner
      })
      .then(({ data }) => data);
  }

  assignRequisitionPONumber(
    id: number,
    po_number: string
  ): Promise<IRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_po_number/`, {
        po_number
      })
      .then(({ data }) => data);
  }

  assignRequisitionApprover(
    id: number,
    approver: number
  ): Promise<IRequisitionData> {
    return this.axios
      .put(`${this.endpoint}${id}/assign_approver/`, {
        approver
      })
      .then(({ data }) => data);
  }

  exportRequisitions(params?: IRequisitionExportFilterParams): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}export-csv/`, {
        responseType: 'blob',
        timeout: REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS,
        params: params
      })
      .then(({ data }) => data);
  }

  exportRequisitionHeader(id: number): Promise<void> {
    return this.axios
      .post(`${this.endpoint}${id}/export_header/`, undefined, {
        responseType: 'blob',
        timeout: REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS
      })
      .then(({ data }) => data);
  }

  exportRequisitionLines(id: number, purchaseOrder: string): Promise<void> {
    return this.axios
      .post(
        `${this.endpoint}${id}/export_lines/`,
        {
          purchase_order: purchaseOrder
        },
        {
          responseType: 'blob',
          timeout: REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS
        }
      )
      .then(({ data }) => data);
  }

  fetchRequisitionActivities(id: number): Promise<Transition[]> {
    return this.axios
      .get<Transition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  // comments
  fetchRequisitionComments(requisitionId: number): Promise<IRequisitionData[]> {
    return this.axios
      .get<IRequisitionData[]>(`${this.endpoint}${requisitionId}/comments/`)
      .then(({ data }) => data);
  }

  fetchRequisitionComment(
    requisitionId: number,
    commentId: number
  ): Promise<IRequisitionData> {
    return this.axios
      .get<IRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createRequisitionComment(
    requisitionId: number,
    comment: IRequisitionCommentData
  ): Promise<IRequisitionData> {
    return this.axios
      .post<IRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateRequisitionComment(
    requisitionId: number,
    comment: IRequisitionCommentData
  ): Promise<IRequisitionData> {
    return this.axios
      .patch<IRequisitionData>(
        `${this.endpoint}${requisitionId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteRequisitionComment(
    requisitionId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${requisitionId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addRequisitionCommentAttachments(
    requisitionId: number,
    commentId: number,
    attachments: IAttachment[]
  ): Promise<IRequisitionCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IRequisitionCommentData>(
        `${this.endpoint}${requisitionId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeRequisitionCommentAttachment(
    requisitionId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${requisitionId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  exportRequisitionPdf(requisitionId: number): Promise<Blob> {
    return this.axios
      .get(`${this.endpoint}${requisitionId}/export_pdf/`, {
        responseType: 'blob',
        timeout: REQUISITION_EXPORT_TIMEOUT_IN_MILLISECONDS
      })
      .then(({ data }) => data);
  }

  updateMultipleRequisitionOwner(
    owner: number,
    requisitions: number[]
  ): Promise<void> {
    return this.axios
      .post<void>(`${this.endpoint}bulk-set-owner/`, {
        requisitions,
        owner
      })
      .then(({ data }) => data);
  }

  fetchRequisitionCommentsMentionableUsers(requisitionId: number) {
    return this.axios
      .get(`${this.endpoint}${requisitionId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }
}
